/**
 * Session Validator Utility
 * Provides utilities for validating user sessions periodically
 */
import authService from '../services/authService';

class SessionValidator {
  constructor() {
    this.validationInterval = null;
    this.validationFrequency = 10 * 60 * 1000; // 10 minutes in milliseconds (increased from 5)
    this.onSessionInvalid = null;
  }

  /**
   * Start periodic session validation
   * @param {Function} onSessionInvalid - Callback function when session becomes invalid
   * @param {number} frequency - Validation frequency in milliseconds (default: 5 minutes)
   */
  startPeriodicValidation(onSessionInvalid, frequency = this.validationFrequency) {
    this.onSessionInvalid = onSessionInvalid;
    this.validationFrequency = frequency;

    // Clear any existing interval
    this.stopPeriodicValidation();

    // Start the validation interval
    this.validationInterval = setInterval(async () => {
      await this.validateCurrentSession();
    }, this.validationFrequency);

    console.log(`Session validation started with ${frequency / 1000}s intervals`);
  }

  /**
   * Stop periodic session validation
   */
  stopPeriodicValidation() {
    if (this.validationInterval) {
      clearInterval(this.validationInterval);
      this.validationInterval = null;
      console.log('Session validation stopped');
    }
  }

  /**
   * Validate the current session
   * @returns {Promise<boolean>} - True if session is valid
   */
  async validateCurrentSession() {
    try {
      // Only validate if user appears to be authenticated
      if (!authService.isAuthenticated()) {
        return false;
      }

      const isValid = await authService.validateSession();
      
      if (!isValid && this.onSessionInvalid) {
        console.warn('Session validation failed, calling invalid session handler');
        this.onSessionInvalid();
      }

      return isValid;
    } catch (error) {
      console.error('Error during periodic session validation:', error);
      return true; // Assume valid to avoid false negatives from network issues
    }
  }

  /**
   * Manually trigger session validation
   * @returns {Promise<boolean>} - True if session is valid
   */
  async checkSession() {
    return this.validateCurrentSession();
  }

  /**
   * Get current validation frequency
   * @returns {number} - Validation frequency in milliseconds
   */
  getValidationFrequency() {
    return this.validationFrequency;
  }

  /**
   * Check if periodic validation is active
   * @returns {boolean} - True if validation is running
   */
  isValidationActive() {
    return this.validationInterval !== null;
  }
}

// Export singleton instance
export default new SessionValidator();
